import type {
  PlanningBlock,
  PlanningBlockReviewStatus,
  PlanningBlockSource,
  PlanningBlockStatus,
  PlanningBlockSyncStatus,
  PlanningBlockType,
} from "../models/production";

const VALID_STATUSES = new Set<PlanningBlockStatus>(["draft", "confirmed", "published", "cancelled"]);
const VALID_SOURCES = new Set<PlanningBlockSource>(["manual", "imported", "ai", "fpt_booking"]);
const VALID_BLOCK_TYPES = new Set<PlanningBlockType>(["project", "leave", "internal", "hold"]);
const VALID_SYNC_STATUSES = new Set<PlanningBlockSyncStatus>(["not_synced", "ready_to_sync", "synced", "sync_failed"]);
const VALID_REVIEW_STATUSES = new Set<PlanningBlockReviewStatus>(["needs_review", "ready", "blocked"]);

export interface PlanningBlockNormalizationOptions {
  existing?: PlanningBlock;
  id: string;
  ownerId: string;
  now: string;
  actorId?: string;
  artistName?: string;
  projectName?: string;
}

export interface PlanningBlockNormalizationResult {
  block?: PlanningBlock;
  errors: string[];
}

export interface PlanningBlockConflictWarning {
  artistId: string;
  date: string;
  allocatedPercent: number;
  availablePercent: number;
  overAllocatedPercent: number;
  blockIds: string[];
}

function cleanString(value: unknown) {
  return typeof value === "string" ? value.trim() : "";
}

function optionalString(value: unknown) {
  const cleaned = cleanString(value);
  return cleaned || undefined;
}

function toIsoDate(value: unknown) {
  const cleaned = cleanString(value);
  if (!/^\d{4}-\d{2}-\d{2}/.test(cleaned)) return "";
  const date = new Date(`${cleaned.slice(0, 10)}T00:00:00.000Z`);
  if (!Number.isFinite(date.getTime())) return "";
  return cleaned.slice(0, 10);
}

function normalizeStatus(value: unknown, fallback: PlanningBlockStatus): PlanningBlockStatus {
  const cleaned = cleanString(value) as PlanningBlockStatus;
  return VALID_STATUSES.has(cleaned) ? cleaned : fallback;
}

function normalizeSource(value: unknown, fallback: PlanningBlockSource): PlanningBlockSource {
  const cleaned = cleanString(value) as PlanningBlockSource;
  return VALID_SOURCES.has(cleaned) ? cleaned : fallback;
}

function normalizeBlockType(value: unknown, fallback: PlanningBlockType): PlanningBlockType {
  const cleaned = cleanString(value) as PlanningBlockType;
  return VALID_BLOCK_TYPES.has(cleaned) ? cleaned : fallback;
}

function normalizeSyncStatus(value: unknown, fallback: PlanningBlockSyncStatus): PlanningBlockSyncStatus {
  const cleaned = cleanString(value) as PlanningBlockSyncStatus;
  return VALID_SYNC_STATUSES.has(cleaned) ? cleaned : fallback;
}

function normalizeReviewStatus(value: unknown, fallback?: PlanningBlockReviewStatus): PlanningBlockReviewStatus | undefined {
  const cleaned = cleanString(value) as PlanningBlockReviewStatus;
  if (VALID_REVIEW_STATUSES.has(cleaned)) return cleaned;
  return fallback;
}

function toAllocationPercent(value: unknown, fallback = 100) {
  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

export function isWeekend(date: string | Date) {
  const parsed = typeof date === "string" ? new Date(`${date.slice(0, 10)}T00:00:00.000Z`) : date;
  const day = parsed.getUTCDay();
  return day === 0 || day === 6;
}

export function getDateRangeDays(startDate: string, endDate: string) {
  const start = new Date(`${startDate}T00:00:00.000Z`);
  const end = new Date(`${endDate}T00:00:00.000Z`);
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || end < start) return [];

  const days: string[] = [];
  const cursor = new Date(start.getTime());
  while (cursor <= end) {
    days.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return days;
}

export function blockOverlapsDate(block: Pick<PlanningBlock, "startDate" | "endDate">, date: string) {
  return Boolean(block.startDate && block.endDate && date >= block.startDate && date <= block.endDate);
}

export function blockOverlapsRange(block: Pick<PlanningBlock, "startDate" | "endDate">, startDate?: string, endDate?: string) {
  if (!startDate && !endDate) return true;
  const effectiveStart = startDate || endDate;
  const effectiveEnd = endDate || startDate;
  if (!effectiveStart || !effectiveEnd) return true;
  return Boolean(block.startDate && block.endDate && block.startDate <= effectiveEnd && block.endDate >= effectiveStart);
}

export function getBlockWorkingDays(block: Pick<PlanningBlock, "startDate" | "endDate">) {
  return getDateRangeDays(block.startDate, block.endDate).filter((date) => !isWeekend(date));
}

export function getArtistDailyAllocation(blocks: PlanningBlock[], artistId: string, date: string) {
  const activeBlocks = blocks.filter(
    (block) =>
      !block.isDeleted &&
      block.status !== "cancelled" &&
      block.artistId === artistId &&
      blockOverlapsDate(block, date) &&
      !isWeekend(date)
  );

  const allocatedPercent = activeBlocks
    .filter((block) => block.blockType !== "leave")
    .reduce((sum, block) => sum + Number(block.allocationPercent || 0), 0);
  const leavePercent = activeBlocks
    .filter((block) => block.blockType === "leave")
    .reduce((sum, block) => sum + Number(block.allocationPercent || 0), 0);
  const availablePercent = Math.max(0, 100 - leavePercent);

  return {
    artistId,
    date,
    allocatedPercent,
    leavePercent,
    availablePercent,
    overAllocatedPercent: Math.max(0, allocatedPercent - availablePercent),
    blocks: activeBlocks,
  };
}

export function getArtistOverAllocationWarnings(blocks: PlanningBlock[]): PlanningBlockConflictWarning[] {
  const keys = new Set<string>();

  for (const block of blocks) {
    if (block.isDeleted || block.status === "cancelled") continue;
    for (const date of getBlockWorkingDays(block)) {
      keys.add(`${block.artistId}:${date}`);
    }
  }

  return Array.from(keys)
    .map((key) => {
      const [artistId, date] = key.split(":");
      const allocation = getArtistDailyAllocation(blocks, artistId, date);
      return {
        artistId,
        date,
        allocatedPercent: allocation.allocatedPercent,
        availablePercent: allocation.availablePercent,
        overAllocatedPercent: allocation.overAllocatedPercent,
        blockIds: allocation.blocks.map((block) => block.id),
      };
    })
    .filter((warning) => warning.overAllocatedPercent > 0)
    .sort((a, b) => a.date.localeCompare(b.date) || a.artistId.localeCompare(b.artistId));
}

export function normalizePlanningBlockInput(input: Record<string, unknown>, options: PlanningBlockNormalizationOptions): PlanningBlockNormalizationResult {
  const existing = options.existing;
  const status = normalizeStatus(input.status, existing?.status || "draft");
  const source = normalizeSource(input.source, existing?.source || "manual");
  const blockType = normalizeBlockType(input.blockType, existing?.blockType || "project");
  const startDate = toIsoDate(input.startDate ?? existing?.startDate);
  const endDate = toIsoDate(input.endDate ?? existing?.endDate);
  const artistId = cleanString(input.artistId ?? existing?.artistId);
  const projectId = optionalString(input.projectId ?? existing?.projectId);
  const allocationPercent = toAllocationPercent(input.allocationPercent ?? existing?.allocationPercent, existing?.allocationPercent ?? 100);
  const syncStatus = normalizeSyncStatus(existing?.syncStatus, "not_synced");
  const reviewStatus = normalizeReviewStatus(input.reviewStatus, existing?.reviewStatus);
  const errors: string[] = [];

  if (!artistId) errors.push("artistId is required.");
  if (!startDate) errors.push("startDate is required.");
  if (!endDate) errors.push("endDate is required.");
  if (startDate && endDate && endDate < startDate) errors.push("endDate must not be before startDate.");
  if (allocationPercent < 0 || allocationPercent > 100) errors.push("allocationPercent must be between 0 and 100.");
  if (blockType === "project" && !projectId) errors.push("projectId is required when blockType is project.");

  if (errors.length) {
    return { errors };
  }

  return {
    errors: [],
    block: {
      id: existing?.id || options.id,
      ownerId: existing?.ownerId || options.ownerId,
      artistId,
      artistName: optionalString(input.artistName) || options.artistName || existing?.artistName,
      projectId,
      projectName: optionalString(input.projectName) || options.projectName || existing?.projectName,
      department: optionalString(input.department ?? existing?.department),
      startDate,
      endDate,
      allocationPercent,
      status,
      blockType,
      note: optionalString(input.note ?? existing?.note),
      source,
      sourceBookingId: optionalString(input.sourceBookingId ?? existing?.sourceBookingId),
      sourceFptBookingId: optionalString(input.sourceFptBookingId ?? existing?.sourceFptBookingId),
      publishedBookingId: optionalString(input.publishedBookingId ?? existing?.publishedBookingId),
      syncStatus,
      externalBookingId: existing?.externalBookingId,
      externalSource: existing?.externalSource,
      lastSyncedAt: existing?.lastSyncedAt,
      syncError: existing?.syncError,
      reviewStatus,
      reviewNote: optionalString(input.reviewNote ?? existing?.reviewNote),
      reviewedBy: optionalString(input.reviewedBy ?? existing?.reviewedBy),
      reviewedAt: optionalString(input.reviewedAt ?? existing?.reviewedAt),
      createdBy: existing?.createdBy || options.actorId,
      updatedBy: options.actorId || existing?.updatedBy,
      isDeleted: existing?.isDeleted ?? false,
      createdAt: existing?.createdAt || options.now,
      updatedAt: options.now,
    },
  };
}
